/**
 * @license Copyright (c) 2023-2025, CKSource Holding sp. z o.o. All rights reserved.
 * For licensing, see LICENSE.md.
 */

import type { ToolbarButton } from '@ckeditor/ckeditor5-metadata';
import { Checkbox, ListItem, Radio, Stack, Typography } from '@mui/material';
import type { ReactNode } from 'react';

import type { PluginItemProps } from './types';
import { premiumPluginTooltip } from '../../../constants/tooltips';
import { HighlightSearch } from '../../HighlightSearch/HighlightSearch';
import { PluginPremiumBadge, PluginDisabledBadge, PluginConflictBadge, PluginDependsBadge } from '../../PluginBadges/PluginBadges';

export function PluginItem( {
	plugin,
	onPluginSelectionChange,
	isSingleFeatureChoice
}: PluginItemProps ): ReactNode {
	const InputElement = isSingleFeatureChoice ? Radio : Checkbox;
	const opacity = plugin.isDisabled ? 0.5 : 1;
	const cursor = plugin.isDisabled ? 'default' : 'pointer';

	return (
		<ListItem>
			<Stack direction='row' alignItems='center' gap='8px' className='plugin-item'>
				<InputElement
					id={ `plugin-${ plugin.className }` }
					value={ plugin.className }
					checked={ plugin.isSelected }
					disabled={ plugin.isDisabled }
					onChange={ () => onPluginSelectionChange( plugin ) }
				/>

				<Typography
					htmlFor={ `plugin-${ plugin.className }` }
					component='label'
					className='plugin-label'
					sx={ { opacity, cursor } }
				>
					{ plugin.uiComponents
						.filter( ( component ): component is ToolbarButton => {
							return component.type === 'Button' || component.type === 'SplitButton';
						} )
						.map( component => (
							<span
								className='plugin-ui-icon'
								key={ component.name }
								dangerouslySetInnerHTML={ { __html: component.svg } }
							/>
						) ) }
					<HighlightSearch style={ { overflow: 'hidden', textOverflow: 'ellipsis' } } title={ plugin.className }>
						{ plugin.className }
					</HighlightSearch>
				</Typography>
				{
					plugin.dependantPlugins.length > 0 &&
					<PluginDependsBadge
						tooltipTitle={ 'This plugin depends on following plugins:' }
						tooltipList={ plugin.dependantPlugins }
						onlyIcon
					/>
				}
				{ plugin.isPremium && <PluginPremiumBadge onlyIcon tooltipTitle={ premiumPluginTooltip }/> }
				{ plugin.isDisabled && <PluginDisabledBadge tooltipList={ plugin.unsupportedEditorTypes }/> }
				{ plugin.hasConflict && !plugin.isDisabled && <PluginConflictBadge tooltipList={ plugin.conflictingPlugins }/> }
			</Stack>
		</ListItem>
	);
}
